import React, { useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';

const BATCH_SIZE = 500;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const BibleJsonUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const versionCode = 'NKRV';
  const versionName = '개역개정';
  const languageCode = 'ko';

  const sampleShape = useMemo(
    () => '{"GEN":{"1":{"1":"태초에 하나님이..."}}}',
    []
  );

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    setFiles(selectedFiles);
    setResult(null);
  };

  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      alert('업로드할 JSON 파일을 선택하세요.');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const allRows = [];
      const allBookCodes = new Set();

      // 모든 파일 처리
      for (const file of files) {
        const text = await file.text();
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch (parseError) {
          alert(`파일 ${file.name} JSON 파싱 오류: ${parseError.message}`);
          setUploading(false);
          return;
        }

        if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
          alert(`파일 ${file.name}: JSON 최상위 구조는 객체여야 합니다.`);
          setUploading(false);
          return;
        }

        const bookCodes = Object.keys(parsed);

        for (const bookCode of bookCodes) {
          allBookCodes.add(bookCode);
          const chapters = parsed[bookCode];
          if (!chapters || typeof chapters !== 'object') {
            continue;
          }

          for (const chapterKey of Object.keys(chapters)) {
            const verses = chapters[chapterKey];
            const chapterNumber = toNumber(chapterKey);
            if (!verses || typeof verses !== 'object' || chapterNumber === null) {
              continue;
            }

            for (const verseKey of Object.keys(verses)) {
              const verseNumber = toNumber(verseKey);
              const verseText = String(verses[verseKey] ?? '').trim();
              if (verseNumber === null || !verseText) {
                continue;
              }

              allRows.push({
                version_code: versionCode,
                book_code: bookCode,
                chapter_number: chapterNumber,
                verse_number: verseNumber,
                verse_text: verseText,
              });
            }
          }
        }
      }

      if (allRows.length === 0) {
        alert('삽입할 구절 데이터가 없습니다. 파일 구조를 확인하세요.');
        setUploading(false);
        return;
      }

      const bookCodes = Array.from(allBookCodes);

      const { error: versionError } = await supabase.from('bible_versions').upsert(
        {
          code: versionCode,
          name: versionName,
          language_code: languageCode,
          is_active: true,
        },
        { onConflict: 'code' }
      );

      if (versionError) {
        throw new Error(`bible_versions 처리 오류: ${versionError.message}`);
      }

      const booksPayload = bookCodes.map((bookCode, index) => ({
        code: bookCode,
        display_name: bookCode,
        testament: index < 39 ? 'OT' : 'NT',
        sort_order: index + 1,
      }));

      const { error: booksError } = await supabase
        .from('bible_books')
        .upsert(booksPayload, { onConflict: 'code' });

      if (booksError) {
        throw new Error(`bible_books 처리 오류: ${booksError.message}`);
      }

      let insertedCount = 0;
      const batches = chunk(allRows, BATCH_SIZE);
      for (const batchRows of batches) {
        const { error: verseError } = await supabase
          .from('bible_version_verses')
          .upsert(batchRows, {
            onConflict: 'version_code,book_code,chapter_number,verse_number',
          });

        if (verseError) {
          throw new Error(`구절 입력 오류: ${verseError.message}`);
        }

        insertedCount += batchRows.length;
      }

      setResult({
        success: true,
        message: `업로드 완료: ${files.length}개 파일, 책 ${bookCodes.length}권, 구절 ${insertedCount}건을 입력(UPSERT)했습니다.`,
      });
      setFiles([]);
    } catch (error) {
      setResult({
        success: false,
        message: error.message || '업로드 중 오류가 발생했습니다.',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3">
        <h6 className="m-0 font-weight-bold text-primary">성경 JSON 입력</h6>
      </div>
      <div className="card-body">
        <form onSubmit={handleUpload}>
          <div className="form-group">
            <label>성경 JSON 파일 (여러 개 선택 가능)</label>
            <input
              type="file"
              className="form-control"
              accept=".json,application/json,text/plain"
              onChange={handleFileChange}
              disabled={uploading}
              multiple
            />
            {files.length > 0 && (
              <small className="form-text text-muted mt-2">
                선택된 파일: <strong>{files.length}개</strong>
                <ul className="mt-1 mb-0">
                  {files.map((f, idx) => (
                    <li key={idx}>{f.name}</li>
                  ))}
                </ul>
              </small>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={files.length === 0 || uploading}>
            {uploading ? '업로드 중...' : 'JSON 입력'}
          </button>
        </form>

        {result && (
          <div className={`mt-3 alert ${result.success ? 'alert-success' : 'alert-danger'}`}>
            {result.message}
          </div>
        )}

        <div className="mt-3">
          <small>
            <b>※ 파일 구조</b><br />
            최상위는 책 코드(예: GEN)이며, 하위는 장/절 객체 구조입니다.<br />
            예시: <code>{sampleShape}</code><br />
            테이블: <code>bible_versions</code>, <code>bible_books</code>, <code>bible_version_verses</code>
          </small>
        </div>
      </div>
    </div>
  );
};

export default BibleJsonUpload;
